import { canvasToBlob, renderThumbnail } from '../photo'
import { deletePhoto, getPhoto, getThumbUrl, putPhoto, type PutOutcome } from '../photoStore'

/** Longest edge kept. A modern phone photo is far larger than the compendium needs. */
const MAX_EDGE = 2048
const QUALITY = 0.85

/**
 * Sellos share the photo store with rendered postcards, prefixed so one
 * discoverable can hold both the imagined and the actual view of the same place.
 */
export function selloKey(id: string): string {
  return `sello:${id}`
}

async function decode(file: File): Promise<CanvasImageSource & { width: number; height: number }> {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' })
    } catch {
      // Safari has shipped partial support; fall through to the img path.
    }
  }
  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    image.decoding = 'async'
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve()
      image.onerror = () => reject(new Error('decode failed'))
      image.src = url
    })
    return image
  } finally {
    URL.revokeObjectURL(url)
  }
}

export interface SelloInput {
  id: string
  placeId: string
  subject: string
  caption: string
}

export type SelloOutcome = PutOutcome | 'unreadable'

/**
 * Downscales and re-encodes the capture. Re-encoding through a canvas also
 * drops EXIF, so the stored image carries no GPS trail or device fingerprint —
 * the app has no reason to keep either.
 */
export async function captureSello(file: File, input: SelloInput): Promise<SelloOutcome> {
  let source: Awaited<ReturnType<typeof decode>>
  try {
    source = await decode(file)
  } catch {
    return 'unreadable'
  }

  const scale = Math.min(1, MAX_EDGE / Math.max(source.width, source.height))
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(source.width * scale))
  canvas.height = Math.max(1, Math.round(source.height * scale))
  const ctx = canvas.getContext('2d')
  if (!ctx) return 'unreadable'
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height)
  if ('close' in source && typeof source.close === 'function') source.close()

  const [full, thumb] = await Promise.all([
    canvasToBlob(canvas, QUALITY),
    renderThumbnail(canvas),
  ])
  if (!full || !thumb) return 'failed'

  return putPhoto({
    id: selloKey(input.id),
    placeId: input.placeId,
    subject: input.subject,
    caption: input.caption,
    takenAt: new Date().toISOString(),
    width: canvas.width,
    height: canvas.height,
    bytes: full.size,
    full,
    thumb: thumb.blob,
  })
}

export function getSelloThumbUrl(id: string): Promise<string | undefined> {
  return getThumbUrl(selloKey(id))
}

export async function hasSello(id: string): Promise<boolean> {
  return (await getPhoto(selloKey(id))) !== undefined
}

export async function getSelloBlob(id: string): Promise<Blob | undefined> {
  return (await getPhoto(selloKey(id)))?.full
}

export function deleteSello(id: string): Promise<void> {
  return deletePhoto(selloKey(id))
}

/** True when a rendered postcard still exists, so the ledger flag must survive. */
export async function hasRenderedPhoto(id: string): Promise<boolean> {
  return (await getPhoto(id)) !== undefined
}
